import { vec3, vec2 } from "gl-matrix";
import { Subject } from "rxjs";

export class TimedMovement<TPath extends PositionObject> {
    public onStep: Subject<Step>;
    public onComplete: Subject<TimedMovement<TPath>>;

    public isActive: boolean;
    private path: TPath[];
    private lastStep: Date;

    constructor(public positionObject: PositionObject, initialNode: TPath, public speed: number) {
        this.onStep = new Subject<Step>();
        this.onComplete = new Subject<TimedMovement<TPath>>();

        this.path = [initialNode];
    }

    public get currentNode(): TPath {
        return this.path.length > 0 ? this.path[0] : null;
    }

    /**
     * Moves the object along the configured path at the given speed. Calling step frequently
     * ensures smooth transitioning between positions. 
     */
    public step() {

        if (!this.isActive) {
            return;
        }

        let currentPosition: vec3 = this.positionObject.position as vec3;

        let elapsedTime = new Date().getTime() - this.lastStep.getTime();
        this.lastStep = new Date();

        if (elapsedTime == 0) {
            // When no time elapsed then no progress has been made.
            return;
        }

        let oldPosition = vec3.clone(currentPosition);

        if (!this.travel(elapsedTime * (this.speed / 1000))) {

            // Path is not yet completed, so emit step event.
            this.onStep.next({ oldPosition: oldPosition, newPosition: currentPosition });
        } else {
            // No path left, we have arrived at the final destination.
            this.isActive = false;
            this.onComplete.next(this);
        }
    }

    /**
     * Moves the object a given distance along the path.
     * @param travelDistance the distance to travel within the current path
     * @returns whether the path was completed.
     */
    private travel(travelDistance: number): boolean {
        if (travelDistance == 0) {
            return false;
        }

        let currentPosition: vec3 = this.positionObject.position as vec3;

        let goalDistance = vec2.dist(currentPosition as any, this.currentNode.position as any);

        if (travelDistance > goalDistance) {
            // Moved further than distance to current goal, so set location to goal location
            vec3.copy(currentPosition, this.currentNode.position);

            if (this.path.length > 1) {
                this.path.shift();
                // Recursively travel the remaining distance.
                return this.travel(travelDistance - goalDistance);
            } else {
                // No path left, we have arrived at the final destination.
                return true;
            }
        } else {
            // Travel the required distance in a straight line.
            let ratio = travelDistance / goalDistance;

            let diff = vec3.sub(vec3.create(), this.currentNode.position, currentPosition);
            vec3.multiply(diff, diff, [ratio, ratio, 0]);
            vec3.add(currentPosition, currentPosition, diff);

            return false;
        }
    }

    /**
     * Sets the movement path for the object.
     * @param pathNodes the nodes of the path the object should be moved along
     */
    public setPath(pathNodes: TPath[]) {
        //this.path = pathNodes.slice();

        if (pathNodes.length > 0) {
            this.path.length = 1;

            this.path.push(...pathNodes);
            this.isActive = true;
            this.lastStep = new Date();
        }
    }

    public clear() {
        this.isActive = false;
        this.path.length = 1;
    }

    public getActivePath(): TPath[] {
        if (this.isActive) {
            return this.path.slice(1);
        }
        else {
            return [];
        }
    }
}

type PositionObject = {
    position: vec3 | number[]
};


type Step = {
    oldPosition: vec3,
    newPosition: vec3
}